import { useContext, useState } from "react";
import isEmpty from "lodash/isEmpty";
import classnames from "classnames";

import ImageMagnify from "@stadium/ui/src/components/image-magnifier";
import ImageCarousel from "@stadium/ui/src/components/multi-carousel/image-carousel";
import ImageCarouselWithMagnifier from "@stadium/ui/src/components/multi-carousel/carousel-with-magnifier";

import useWithAnalytics from "@stadium/services/analytics/hooks/use-with-analytics";

import { useStoreContext } from "@components/store/provider";
import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";

import { ANALYTICS_TARGET_IMAGE } from "@constants/analytics/shop";
import NoImage from "@assets/images/no-image.svg";

import stylesSlider from "./product-detail-slider.module.scss";
import stylesNoImage from "./product-detail-slider-noimage.module.scss";

import { getProductType, getProductCategory } from "@helpers/products/types";

const ProductCarousel = ({
  sliderImages,
  selectedImageIndex = 0,
  setSelectedImageIndex = () => {},
  carouselContainerClass = "",
  thumbnailContainterClass = "",
  imageMagnify = false,
  imageCoverRequired = false,
  productId,
  product,
  imageAlt = "",
}) => {
  const withAnalytics = useWithAnalytics();

  const { isMobileDevice } = useContext(CurrentDeviceContext);
  const [isCarouselSwipeable, setIsCarouselSwipeable] = useState(true);
  const { isPreviewCatalogMode } = useStoreContext();

  const onImageInteraction = ({ type = "click" } = {}) => {
    withAnalytics(undefined, {
      target: ANALYTICS_TARGET_IMAGE,
      track: true,
      eventProperties: {
        product_id: productId,
        interaction_type: type,
        product_type: getProductType(product),
        category: getProductCategory(product),
      },
    })();
  };

  const handleCarouselChange = (index) => {
    if (index === selectedImageIndex) return;
    setSelectedImageIndex(index);
    onImageInteraction({ type: "slide_change" });
  };

  const onZoomStartHandler = () => {
    setIsCarouselSwipeable(false);
    onImageInteraction({ type: "zoom" });
  };
  const onZoomEndHandler = () => setIsCarouselSwipeable(true);

  const showImageMagnifier = imageMagnify && !isMobileDevice;

  //ToDo: Debug why Carousel not working with Multimedia
  const renderImg = (image) => (
    <img
      alt={image ? imageAlt : "No Product Img"}
      src={image || NoImage}
      height={""}
      className={`img-fluid ${image ? "slide-img" : "noImage d-inline-block"}`}
    />
  );

  const renderCustomThumbs = () =>
    sliderImages.map(({ id, image }) => (
      <div key={`product-${id}`} className={stylesSlider.slideImageBox}>
        {renderImg(image)}
      </div>
    ));

  const renderImageContent = (image) =>
    showImageMagnifier ? (
      <div onClick={() => onImageInteraction()}>
        <ImageMagnify
          src={image || NoImage}
          alt={image ? imageAlt : "No Product Img"}
          enlargedImagePosition={isMobileDevice ? "over" : "beside"}
          className={classnames({
            [stylesSlider.imageMagnified]: true,
            [stylesSlider.withImage]: Boolean(image),
            [stylesSlider.objectFitCover]: Boolean(image) && imageCoverRequired,
            [stylesSlider.objectFitContain]: Boolean(image) && !imageCoverRequired,
            [stylesNoImage.withoutImage]: !image,
          })}
          onZoomStart={onZoomStartHandler}
          onZoomEnd={onZoomEndHandler}
        />
      </div>
    ) : (
      <div
        className={image ? stylesSlider.withImage : stylesNoImage.withoutImage}
        onClick={() => onImageInteraction()}
      >
        {renderImg(image)}
      </div>
    );

  const renderImageCarousel = () => (
    <ImageCarousel
      selectedItem={selectedImageIndex}
      onChange={handleCarouselChange}
      thumbWidth={142}
      swipeable={isCarouselSwipeable}
      className={stylesSlider.imageMagnified}
      {...(imageMagnify ? { renderCustomThumbs } : {})}
    >
      {sliderImages.map(({ id, image }) => (
        <div key={`product-${id}`} className={stylesSlider.slideImageBox}>
          {renderImageContent(image)}
        </div>
      ))}
    </ImageCarousel>
  );

  const renderImageCarouselWithMagnifier = () => (
    <ImageCarouselWithMagnifier
      selectedItem={selectedImageIndex}
      thumbWidth={142}
      onChange={handleCarouselChange}
      customMagnifierZoomClass={stylesSlider.customContainerMagnifier}
      className={classnames(stylesSlider.customImageContainerMagnifier, {
        [stylesSlider.catalogImageContainer]: isPreviewCatalogMode,
      })}
      carouselContainerClass={carouselContainerClass}
      thumbnailContainterClass={thumbnailContainterClass}
      magnifierContentClass={stylesSlider.magnifierWithFlag}
      images={sliderImages.map(({ id, image }) => ({
        key: `product-${id}`,
        imageSrc: image || NoImage,
        alt: image ? imageAlt : "No Product Img",
      }))}
      isVerticalAxis={!isMobileDevice}
      onZoomStart={() => onImageInteraction({ type: "zoom" })}
      onClickItem={() => onImageInteraction()}
    />
  );

  const renderImageCarouselContent = () => {
    if (imageMagnify && !isMobileDevice) return renderImageCarouselWithMagnifier();
    return renderImageCarousel();
  };

  const renderImages = () => {
    if (isEmpty(sliderImages)) return renderImageContent();
    if (sliderImages.length == 1) return renderImageContent(sliderImages[0].image);
    return renderImageCarouselContent();
  };

  return (
    <div
      className={classnames({
        ["item-variant-slider"]: true,
        [stylesSlider.imgVariantSlider]: true,
        [stylesNoImage.imgVariantBlock]: true,
        [stylesSlider.withCarouselRevamp]: true,
      })}
    >
      {renderImages()}
    </div>
  );
};

export default ProductCarousel;
